/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import CommissionSearch from "./components/CommissionSearch";
import CommissionList from "./components/CommissionList";
import CommissionToolbar from "./components/CommissionToolbar";
import CommissionExportModal from "./components/CommissionExportModal";
import SweetAlert2 from "react-sweetalert2";
import { Box } from "@chakra-ui/react";
import moment from "moment";

interface Filters {
  [key: string]: any;
}

interface PayloadType {
  [key: string]: any;
}

const rowPerPage = 10;

const initFrom = moment().subtract(1, "month");
const initTo = moment();
const initialDateRangeFilter = [initFrom.toDate(), initTo.toDate()];

const initialFilters: Filters = {
  from: initFrom,
  to: initTo,
  asset: "",
  status: "",
};

const initialDateFiltersForExport: Filters = {
  from: initFrom.format("MMM D, YYYY 00:00"),
  to: initTo.format("MMM D, YYYY 23:59"),
};

const AgentCommission = ({ userPermissions }: { userPermissions: any }) => {
  const [swalProps, setSwalProps] = useState<any>({});
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isFilterLoading, setIsFilterLoading] = useState<boolean>(true);
  const [isReadyToExport, setIsReadyToExport] = useState<boolean>(true);
  const [isOpenExportModal, setIsOpenExportModal] = useState<boolean>(false);
  const [tableData, setTableData] = useState([]);
  const [filters, setFilters] = useState(initialFilters);
  const [dateFiltersForExport, setDateFiltersForExport] = useState(
    initialDateFiltersForExport
  );
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(rowPerPage);
  const [rowCount, setRowCount] = useState(rowPerPage);

  const [selectedDates, setSelectedDates] = useState<Date[]>(initialDateRangeFilter);
  const agent_id = localStorage.getItem("agentId");

  // Control filter toggle
  const [showFilters, setShowFilters] = useState(false);

  // AbortController for cancelling requests on unmount
  const abortControllerRef = useRef<AbortController | null>(null);
  // Request ID to track the latest request and prevent stale updates
  const requestIdRef = useRef(0);

  // Cleanup: Cancel all pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // Create new AbortController for each batch of requests
  const getAbortSignal = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    return abortControllerRef.current.signal;
  };

  useEffect(() => {
    const signal = getAbortSignal();
    // Increment request ID to invalidate any previous requests
    const currentRequestId = ++requestIdRef.current;

    const fetchData = async () => {
      setIsLoading(true);
      await fetchCommissions({ page: 0, limit: rowPerPage, ...filters }, false, signal, currentRequestId);
      if (signal.aborted || requestIdRef.current !== currentRequestId) return;

      setIsLoading(false);
      setIsFilterLoading(false);
    };

    fetchData();
  }, []); // eslint-disable-line

  const fetchCommissions = async (
    payload: PayloadType = {},
    isReset = false,
    signal?: AbortSignal,
    requestId?: number
  ) => {
    try {
      const formattedFromDate = moment(
        !isReset ? selectedDates[0] : initFrom,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 00:00");
      const formattedToDate = moment(
        !isReset ? selectedDates[1] : initTo,
        "DD/MM/YYYY"
      ).format("YYYY-MM-DD 23:59");

      const queryObject: PayloadType = {
        transaction_type: "reseller_fee_received",
        limit: payload?.limit,
        page: payload?.page + 1,
        from: formattedFromDate,
        to: formattedToDate,
        reseller_id: agent_id,
        display_mode: "agent"
      };

      if (payload?.status !== "" && payload?.status !== undefined) {
        queryObject.status = payload?.status;
      }

      if (payload?.asset !== "" && payload?.asset !== undefined) {
        queryObject.asset = payload?.asset;
      }

      const queryString = Object.keys(queryObject)
        .map(
          (key) =>
            `${encodeURIComponent(key)}=${encodeURIComponent(queryObject[key])}`
        )
        .join("&");

      const response = await axios.get(
        process.env.REACT_APP_API_URL +
        `/api/transaction/get-all-transactions?${queryString}`,
        { signal }
      );

      // Don't update state if request was aborted or a newer request was started
      if (signal?.aborted) return;
      if (requestId !== undefined && requestIdRef.current !== requestId) return;

      let newData = response.data.transactions;
      let totalCommissions = response.data.totalTransactions;

      if (newData && newData.length !== 0) {
        setTableData(newData);
      } else {
        setTableData([]);
      }

      setIsLoading(false);
      setRowCount(totalCommissions);
    } catch (error: any) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') {
        return;
      }
      // Don't update state if a newer request was started
      if (requestId !== undefined && requestIdRef.current !== requestId) return;
      setTableData([]);
      setIsLoading(false);
      // Clear all
      setPage(0);
      setPageSize(0);
      setRowCount(0);
      console.error("Error fetching data", error);
    }
  };

  const setPaginationModel = (newValue: any) => {
    setIsLoading(true);

    let newFilters: any = {
      page: newValue.page,
      limit: newValue.pageSize,
      ...filters
    };

    // if select new row per page, reset page to 0, reset lastkey
    if (newValue.pageSize !== pageSize) {
      newFilters = {
        page: 0,
        limit: newValue.pageSize,
        ...filters
      };
      setPage(0);
      setPageSize(newValue.pageSize);
    } else {
      setPage(newValue.page);
    }

    fetchCommissions(newFilters);
  };

  const handleSearchSubmit = async () => {
    setIsReadyToExport(true);
    setIsLoading(true);
    setIsFilterLoading(true);
    setPage(0);
    await fetchCommissions({ page: 0, limit: rowPerPage, ...filters });
    setIsFilterLoading(false);
  };

  const handleSearchClear = async () => {
    setIsReadyToExport(true);
    setIsLoading(true);
    setIsFilterLoading(true);
    setFilters(initialFilters);
    setPage(0);
    setSelectedDates(initialDateRangeFilter);
    setDateFiltersForExport(initialDateFiltersForExport);
    await fetchCommissions(
      { page: 0, limit: rowPerPage, ...initialFilters },
      true
    );
    setIsFilterLoading(false);
  };

  const triggerReloadCommissionList = async () => {
    setIsLoading(true);
    setIsFilterLoading(true);
    await fetchCommissions({
      page: page,
      limit: rowPerPage,
      ...filters,
    });
    setIsFilterLoading(false);
  };

  const handleExport = () => {
    showExportModal();
  };

  const showExportModal = () => {
    setIsOpenExportModal(true);
  };

  const closeExportModal = () => {
    setIsOpenExportModal(false);
  };

  return (
    <>
      <CommissionExportModal
        setSwalProps={setSwalProps}
        isOpen={isOpenExportModal}
        onClose={() => closeExportModal()}
        userPermissions={userPermissions}
        filters={filters}
        isReadyToExport={isReadyToExport}
        dateFiltersForExport={dateFiltersForExport}
      />

      <Box sx={{ position: "relative" }}>
        <CommissionToolbar
          isLoading={isLoading || isFilterLoading}
          onToggleFilters={() => setShowFilters((p) => !p)}
          onRefresh={triggerReloadCommissionList}
          onExport={handleExport}
        />
        <CommissionSearch
          showFilters={showFilters}
          isFilterLoading={isFilterLoading}
          filters={filters}
          setFilters={setFilters}
          selectedDates={selectedDates}
          setSelectedDates={setSelectedDates}
          handleSearchSubmit={handleSearchSubmit}
          handleSearchClear={handleSearchClear}
          setIsReadyToExport={setIsReadyToExport}
          setDateFiltersForExport={setDateFiltersForExport}
        />
        <CommissionList
          isLoading={isLoading}
          page={page}
          pageSize={pageSize}
          rowCount={rowCount}
          tableData={isLoading ? [] : tableData}
          setPaginationModel={setPaginationModel}
        />
      </Box>

      <SweetAlert2
        {...swalProps}
        customClass={{
          actions: "custom-swal2-popup-action",
          title: "custom-swal2-popup-title",
          confirmButton:
            "custom-swal2-popup-action custom-swal2-popup-action-confirm",
          cancelButton:
            "custom-swal2-popup-action custom-swal2-popup-action-cancel",
        }}
      ></SweetAlert2>
    </>
  );
};

export default AgentCommission;
